import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Filter, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const FR_MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

// Phase 7C addendum — Cursus Physio target cities.
const PRIORITY_CITIES = ["Lyon", "Nice", "Rennes", "Bordeaux", "Strasbourg", "Montpellier", "Lille", "Orléans", "Toulouse"];

function dispoColor(dispo, hasSession) {
    if (hasSession) return "bg-brand-orange text-white";
    switch (dispo) {
        case "full_day": return "bg-blue-500 text-white";
        case "morning": return "bg-green-200 text-green-900";
        case "afternoon": return "bg-yellow-200 text-yellow-900";
        default: return "bg-gray-100 text-gray-400";
    }
}

function dispoLabel(dispo, hasSession, sessionInfo) {
    if (hasSession) return `Session : ${sessionInfo.formation_name} · ${sessionInfo.city || "?"}`;
    switch (dispo) {
        case "full_day": return "Disponible journée";
        case "morning": return "Disponible matin";
        case "afternoon": return "Disponible après-midi";
        default: return "Indisponible / non renseigné";
    }
}

export default function AdminPlanning() {
    const navigate = useNavigate();
    const [now] = useState(new Date());
    // Default to current month — replaced async by busiest-month auto-nav (Module 11)
    const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    const [autoNavDone, setAutoNavDone] = useState(false);
    const [cursusSlug, setCursusSlug] = useState("");
    const [city, setCity] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cursusList, setCursusList] = useState([]);

    useEffect(() => {
        api.get("/cursus").then((r) => setCursusList(r.data || [])).catch(() => {});
    }, []);

    // Module 11 — Auto-navigate to the upcoming month with the most sessions
    useEffect(() => {
        if (autoNavDone) return;
        const today = new Date().toISOString().slice(0, 10);
        api.get(`/sessions?from_date=${today}`)
            .then((r) => {
                const counts = {};
                (r.data || []).forEach((s) => {
                    if (!s.start_date) return;
                    const m = s.start_date.slice(0, 7);
                    counts[m] = (counts[m] || 0) + 1;
                });
                const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
                if (best && best[0] !== month) {
                    setMonth(best[0]);
                    toast.message(`Auto-navigation : ${best[1]} sessions en ${best[0]}`);
                }
            })
            .catch(() => {})
            .finally(() => setAutoNavDone(true));
    }, [autoNavDone, month]);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams({ month });
        if (cursusSlug) params.set("cursus_slug", cursusSlug);
        if (city) params.set("city", city);
        api.get(`/admin/planning/matrix?${params.toString()}`)
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [month, cursusSlug, city]);

    const navigateMonth = (delta) => {
        const [y, m] = month.split("-").map(Number);
        const d = new Date(y, m - 1 + delta, 1);
        setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    };

    const monthLabel = useMemo(() => {
        const [y, m] = month.split("-").map(Number);
        return `${FR_MONTHS[m - 1]} ${y}`;
    }, [month]);

    const handleCellClick = (formateur, cell) => {
        if (cell.session) {
            navigate(`/admin/sessions`);
            return;
        }
        if (cell.dispo) {
            // Pre-fill the wizard
            navigate(`/admin/sessions/nouvelle?date=${cell.date}&formateur_id=${formateur.id}`);
        }
    };

    return (
        <div className="mx-auto max-w-[100rem] px-6 py-10" data-testid="page-admin-planning">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Pilotage</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Planning formateurs</h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Disponibilités déclarées + sessions assignées. Cliquez sur une cellule disponible pour pré-remplir le wizard de création.
                    </p>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <Calendar className="h-7 w-7" />
                </div>
            </div>

            {/* Filters & nav */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white p-1">
                    <button onClick={() => navigateMonth(-1)} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-brand-line/30" data-testid="planning-prev-month">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="px-4 font-display text-base" data-testid="planning-month-label">{monthLabel}</span>
                    <button onClick={() => navigateMonth(1)} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-brand-line/30" data-testid="planning-next-month">
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
                <select
                    value={cursusSlug}
                    onChange={(e) => setCursusSlug(e.target.value)}
                    data-testid="planning-filter-cursus"
                    className="rounded-full border border-brand-line bg-white px-4 py-2 text-sm outline-none focus:border-brand-orange"
                >
                    <option value="">Tous cursus</option>
                    {cursusList.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <input
                    placeholder="Filtrer par ville…"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    data-testid="planning-filter-city"
                    className="w-40 rounded-full border border-brand-line bg-white px-4 py-2 text-sm outline-none focus:border-brand-orange"
                />
                <select
                    onChange={(e) => setCity(e.target.value)}
                    value={PRIORITY_CITIES.includes(city) ? city : ""}
                    data-testid="planning-filter-priority"
                    className="rounded-full border border-brand-line bg-white px-4 py-2 text-sm outline-none focus:border-brand-orange"
                    title="Sauter directement vers une ville cible Cursus Physio"
                >
                    <option value="">🎯 Villes cibles…</option>
                    {PRIORITY_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="ml-auto flex items-center gap-3 text-[10px] text-brand-smoke">
                    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-blue-500" /> Journée</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-green-200" /> Matin</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-yellow-200" /> Après-midi</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-brand-orange" /> Session assignée</span>
                </div>
            </div>

            {/* Matrix */}
            <div className="mt-6 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium">
                {loading ? (
                    <div className="py-20 text-center text-brand-smoke">Chargement de la matrice…</div>
                ) : !data ? (
                    <div className="py-20 text-center text-brand-smoke">Aucune donnée.</div>
                ) : (
                    <div className="overflow-x-auto" data-testid="planning-matrix">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-brand-line bg-[#FAFAFA]">
                                    <th className="sticky left-0 z-10 min-w-[180px] bg-[#FAFAFA] px-4 py-3 text-left font-semibold uppercase tracking-wider text-brand-smoke">
                                        Formateur
                                    </th>
                                    {data.days.map((d) => {
                                        const day = parseInt(d.split("-")[2], 10);
                                        const dow = new Date(d).getDay();
                                        const weekend = dow === 0 || dow === 6;
                                        return (
                                            <th
                                                key={d}
                                                className={`min-w-[34px] px-1 py-3 text-center font-semibold ${weekend ? "bg-brand-line/40 text-brand-smoke" : "text-brand-ink"}`}
                                            >
                                                {day}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-brand-line">
                                {data.rows.length === 0 ? (
                                    <tr><td colSpan={data.days.length + 1} className="py-10 text-center text-brand-smoke">Aucun formateur ne correspond aux filtres.</td></tr>
                                ) : data.rows.map((r) => (
                                    <tr key={r.formateur.id} data-testid={`planning-row-${r.formateur.id}`}>
                                        <td className="sticky left-0 z-10 bg-white px-4 py-2 align-middle">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-brand-ink">{r.formateur.first_name} {r.formateur.last_name}</span>
                                                {r.formateur.is_demo_account && (
                                                    <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px] font-semibold uppercase text-gray-600">Démo</span>
                                                )}
                                            </div>
                                        </td>
                                        {r.cells.map((cell) => (
                                            <td key={cell.date} className="p-0.5 align-middle">
                                                <button
                                                    onClick={() => handleCellClick(r.formateur, cell)}
                                                    title={dispoLabel(cell.dispo, !!cell.session, cell.session)}
                                                    data-testid={`planning-cell-${r.formateur.id}-${cell.date}`}
                                                    disabled={!cell.dispo && !cell.session}
                                                    className={`flex h-7 w-full items-center justify-center rounded text-[9px] ${dispoColor(cell.dispo, !!cell.session)} ${(cell.dispo || cell.session) ? "cursor-pointer hover:ring-2 hover:ring-brand-orange" : "cursor-default"}`}
                                                >
                                                    {cell.session ? "•" : ""}
                                                </button>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
